import React, { useEffect } from 'react';
import { makeStyles, Theme } from '@material-ui/core/styles';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import { FormControlLabel, Checkbox } from '@material-ui/core';
import { useSnackbar } from 'notistack';
import { DictionaryDto, useContentApi } from 'api/dictionary';
import Spin from 'components/atoms/Spin/Spin';
import {
    EndpointDto,
    EndpointRoleDto,
    initEndpointRole,
    createEndpointLink,
    removeEndpointLink,
} from 'api/endpoints';

interface TabPanelProps {
    children?: React.ReactNode;
    index: any;
    value: any;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
        <Box
            flexGrow={1}
            role="tabpanel"
            hidden={value !== index}
            id={`vertical-tabpanel-${index}`}
            aria-labelledby={`vertical-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box p={2} paddingTop={0}>
                    {children}
                </Box>
            )}
        </Box>
    );
}

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        flexGrow: 1,
        backgroundColor: theme.palette.background.paper,
        display: 'flex',
    },
    tabs: {
        borderRight: `1px solid ${theme.palette.divider}`,
        minWidth: '200px',
    },
    list: {
        display: 'block',
        listStyle: 'none',
        margin: '0',
        padding: theme.spacing(1),
        columns: 2,
        [theme.breakpoints.down('sm')]: {
            columns: 1,
        },
        '&>li': {},
    },
    title: {
        margin: '0',
        paddingTop: '0',
    },
}));

export default function RoleAccesses() {
    const classes = useStyles();
    const { enqueueSnackbar } = useSnackbar();
    const [value, setValue] = React.useState(0);
    const [roles, setRoles] = React.useState<DictionaryDto[]>([]);
    const [loading, setLoading] = React.useState(false);
    const [endpoints, setEndpoints] = React.useState<EndpointDto[]>([]);
    const [endpointRoles, setEndpointRoles] = React.useState<EndpointRoleDto[]>([]);
    const [roleCheckedValues, setRoleCheckedValues] = React.useState<{
        [key: string]: any[];
    }>({});
    const { fetchAll: fetchRolesDict } = useContentApi<DictionaryDto>('/user-role');
    const { fetchAll: fetchEndpoints } = useContentApi<EndpointDto>('/endpoint');
    const { fetchAll: fetchEndpointRoles } = useContentApi<EndpointRoleDto>('/endpoint-role-link');

    // @ts-ignore
    const handleChange = (event: React.ChangeEvent<{}>, newValue: number) => {
        setValue(newValue);
    };

    const getRoleCheckedEndpoints = (endpointList: EndpointRoleDto[]): { [key: string]: any[] } => {
        const mappings: { [key: string]: any[] } = {};

        endpointList.forEach((role) => {
            const roleId: string = role.user_role.id.toString();
            mappings[roleId] = mappings[roleId] || [];
            mappings[roleId].push(role.endpoint.id.toString());
        });

        return mappings;
    };

    const getCheckedValue = (roleId: string, endpointId: string) => {
        if (!roleCheckedValues[roleId]) {
            return false;
        }

        return roleCheckedValues[roleId].includes(endpointId);
    };

    const setCheckedValue = (roleId: string, endpointId: string) => {
        const index = endpointRoles.findIndex(
            (er) =>
                er.user_role.id.toString() === roleId && er.endpoint.id.toString() === endpointId
        );
        const newEndpointRoles = endpointRoles.concat();

        if (index === -1) {
            newEndpointRoles.push(initEndpointRole(roleId, endpointId));
            createEndpointLink(roleId, endpointId)
                .then((res) => {
                    const newIndex = endpointRoles.findIndex(
                        (er) =>
                            er.user_role.id.toString() === roleId &&
                            er.endpoint.id.toString() === endpointId
                    );

                    if (newIndex !== -1) {
                        const newItem = endpointRoles[newIndex];
                        newItem.id = res.id;
                        const items = endpointRoles.concat();
                        items[newIndex] = newItem;
                        setRoleCheckedValues(getRoleCheckedEndpoints(items));
                    }
                })
                .catch(() => enqueueSnackbar('Ошибка при сохранении данных', { variant: 'error' }));
        } else {
            newEndpointRoles.splice(index, 1);
            removeEndpointLink(roleId, endpointId).then(() => {});
        }

        setEndpointRoles(newEndpointRoles);
        setRoleCheckedValues(getRoleCheckedEndpoints(newEndpointRoles));
    };

    useEffect(() => {
        setLoading(true);
        Promise.all([fetchRolesDict(), fetchEndpoints(), fetchEndpointRoles()])
            .then((res) => {
                setRoles(res[0].filter((r) => r.code !== 'ADMIN'));
                setEndpoints(
                    res[1].sort((a, b) => {
                        if (a.code < b.code) {
                            return -1;
                        }

                        if (a.code > b.code) {
                            return 1;
                        }

                        return 0;
                    })
                );
                setEndpointRoles(res[2]);
                setRoleCheckedValues(getRoleCheckedEndpoints(res[2]));
            })
            .finally(() => setLoading(false));
    }, [fetchEndpointRoles, fetchEndpoints, fetchRolesDict]);

    return (
        <div className={classes.root}>
            {loading && <Spin />}
            <Tabs
                orientation="vertical"
                variant="scrollable"
                value={value}
                onChange={handleChange}
                aria-label="Vertical tabs example"
                className={classes.tabs}
            >
                {roles.map((role, index) => (
                    <Tab key={index} label={role.description} />
                ))}
            </Tabs>
            {roles.map((role, index) => {
                return (
                    <TabPanel key={role.code} value={value} index={index}>
                        <Typography variant="h6" className={classes.title}>
                            Права доступа для роли <strong>{role.description}</strong>
                        </Typography>
                        <ul className={classes.list}>
                            {endpoints.map((endpoint, index) => {
                                return (
                                    <li key={index}>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    value={endpoint.id}
                                                    color="primary"
                                                    checked={getCheckedValue(
                                                        role.id.toString(),
                                                        endpoint.id.toString()
                                                    )}
                                                    onChange={() => {
                                                        setCheckedValue(
                                                            role.id.toString(),
                                                            endpoint.id.toString()
                                                        );
                                                    }}
                                                />
                                            }
                                            label={endpoint.code}
                                        />
                                    </li>
                                );
                            })}
                        </ul>
                    </TabPanel>
                );
            })}
        </div>
    );
}
